import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { gameReducer, initialGameState } from './gameReducer';
import { StorageService } from '../services/storageService';

export const GameContext = createContext();

const SAVE_INTERVAL_MS = 3000; // flush every 3 s — not every 100 ms tick

export const GameProvider = ({ children }) => {
  // ── Lazy initializer: runs ONCE before the first render ────────────────────
  const initializer = () => {
    try {
      StorageService.init();
      const saved = StorageService.loadGameState();
      if (saved) {
        console.log('🌱 Loaded from storage. Seeds:', saved.seeds);
        return { ...initialGameState, ...saved };
      }
    } catch (err) {
      console.error('❌ Failed to load game state:', err);
    }
    console.log('🆕 Starting fresh game state');
    return initialGameState;
  };

  const [state, dispatch] = useReducer(gameReducer, undefined, initializer);

  // stateRef always mirrors the latest state so intervals/event-handlers
  // can read it without becoming stale or needing to be in a dep-array.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ── Periodic auto-save (every 3 s) ────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      const ok = StorageService.saveGameState(stateRef.current);
      if (ok) console.log('💾 Auto-saved. Seeds:', Math.floor(stateRef.current.seeds));
    }, SAVE_INTERVAL_MS);

    const saveNow = () => StorageService.saveGameState(stateRef.current);
    window.addEventListener('beforeunload', saveNow);

    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', saveNow);
      StorageService.saveGameState(stateRef.current); // final save on unmount
    };
  }, []); // ← empty dep array — uses stateRef so never needs to re-run

  // ── Save when tab is hidden (user switches away) ──────────────────────────
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        StorageService.saveGameState(stateRef.current);
        console.log('💾 Saved on tab hide. Seeds:', Math.floor(stateRef.current.seeds));
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, []);

  // ── Dispatch helpers (stable references via useCallback) ──────────────────
  const tick          = useCallback(() => dispatch({ type: 'TICK' }),           []);
  const harvest       = useCallback(() => dispatch({ type: 'HARVEST' }),        []);
  const buyPlanter    = useCallback(() => dispatch({ type: 'BUY_PLANTER' }),    []);
  const buyGardener   = useCallback(() => dispatch({ type: 'BUY_GARDENER' }),   []);
  const buyEfficiency = useCallback(() => dispatch({ type: 'BUY_EFFICIENCY' }), []);
  const buyAutomation = useCallback(() => dispatch({ type: 'BUY_AUTOMATION' }), []);
  const prestige      = useCallback(() => dispatch({ type: 'PRESTIGE' }),       []);
  const stopFocus     = useCallback(() => dispatch({ type: 'STOP_FOCUS' }),     []);
  const pauseFocus    = useCallback(() => dispatch({ type: 'PAUSE_FOCUS' }),    []);
  const resumeFocus   = useCallback(() => dispatch({ type: 'RESUME_FOCUS' }),   []);
  const focusTick     = useCallback(() => dispatch({ type: 'FOCUS_TICK' }),     []);
  const claimDaily    = useCallback(() => dispatch({ type: 'CLAIM_DAILY' }),    []);
  const updatePlots   = useCallback(() => dispatch({ type: 'UPDATE_PLOTS' }),   []);
  
  // Pass stateRef.current so backups/exports always use what's exactly in memory right now
  const createBackup  = useCallback(() => StorageService.createEmergencyBackup(stateRef.current), []);
  const exportGame    = useCallback(() => StorageService.exportUserData(stateRef.current), []);

  const getRecoveryInfo = useCallback(() => StorageService.getRecoveryInfo(),   []);
  const forceRecovery   = useCallback(() => StorageService.forceRecovery(),     []);

  const startFocus    = useCallback((length, scene, artSeed, intention, metadata) => dispatch({ type: 'START_FOCUS', payload: { length, scene, artSeed, intention, metadata } }), []);
  const attachFocusMetadata = useCallback((metadata) => dispatch({ type: 'ATTACH_FOCUS_METADATA', payload: metadata }), []);
  const waterGarden   = useCallback((chargeLevel)    => dispatch({ type: 'WATER_GARDEN',     payload: { chargeLevel } }),   []);
  const startPlot     = useCallback((index)          => dispatch({ type: 'START_PLOT',        payload: { index } }),         []);
  const claimPlot     = useCallback((index)          => dispatch({ type: 'CLAIM_PLOT',        payload: { index } }),         []);
  const abortPlot     = useCallback((index)          => dispatch({ type: 'ABORT_PLOT',        payload: { index } }),         []);
  const setPlotMinutes= useCallback((index, minutes) => dispatch({ type: 'SET_PLOT_MINUTES',  payload: { index, minutes } }),[]);
  const setFocusLength= useCallback((length)         => dispatch({ type: 'SET_FOCUS_LENGTH',  payload: length }),            []);
  const updateProfile = useCallback((profile)        => dispatch({ type: 'UPDATE_PROFILE',    payload: profile }),           []);
  const unlockMentor  = useCallback((mentor, cost)   => dispatch({ type: 'UNLOCK_MENTOR',     payload: { mentor, cost } }), []);
  const upgradeMentor = useCallback((mentorKey)      => dispatch({ type: 'UPGRADE_MENTOR',    payload: { mentorKey } }),     []);
  const refreshData   = useCallback((mentor)         => dispatch({ type: 'REFRESH_DATA',      payload: { mentor } }),        []);
  const loadState     = useCallback((payload)        => dispatch({ type: 'LOAD_STATE',        payload }),                    []);

  const importGame    = useCallback((jsonString) => {
    StorageService.importUserData(jsonString);
    setTimeout(() => window.location.reload(), 500);
  }, []);
  
  const resetGame     = useCallback(() => {
    StorageService.createEmergencyBackup(stateRef.current);
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('mindgarden')) localStorage.removeItem(key);
    });
    window.location.reload();
  }, []);

  // ── Memoized actions object — same reference every render ─────────────────
  const actions = useMemo(() => ({
    tick, harvest, buyPlanter, buyGardener, buyEfficiency, buyAutomation,
    prestige, startFocus, attachFocusMetadata, stopFocus, pauseFocus, resumeFocus, focusTick, claimDaily, waterGarden,
    startPlot, updatePlots, claimPlot, abortPlot, setPlotMinutes,
    setFocusLength, updateProfile, unlockMentor, upgradeMentor, refreshData,
    loadState, exportGame, importGame, resetGame,
    createBackup, getRecoveryInfo, forceRecovery,
  }), [
    tick, harvest, buyPlanter, buyGardener, buyEfficiency, buyAutomation,
    prestige, startFocus, attachFocusMetadata, stopFocus, pauseFocus, resumeFocus, focusTick, claimDaily, waterGarden,
    startPlot, updatePlots, claimPlot, abortPlot, setPlotMinutes,
    setFocusLength, updateProfile, unlockMentor, upgradeMentor, refreshData,
    loadState, exportGame, importGame, resetGame,
    createBackup, getRecoveryInfo, forceRecovery,
  ]);

  return (
    <GameContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) throw new Error('useGame must be used within a GameProvider');
  return context;
};